// StudentIDGenerator.jsx
// ID-card editor for the student profile. Loads the existing ID-card design
// (course title auto-loaded under the name), lets the admin drag/edit/redesign
// the SVG, save the design as a reusable template, and issue the ID card to
// the student (persisted to course_enrollment.id_card + id_cards, shown on the
// user's course dashboard).

import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import dateFormat from "dateformat";
import { AuthContext } from "../../context/AuthContext";
import DefaultAlertModal from "../../components/modals/DeafaultAlertModal";
import DetectDevice from "../../context/DetectDevice";
import SvgTemplateEditor from "../../components/svg-editor/SvgTemplateEditor";
import { substituteTokens, reverseTokens, getViewBox, svgToPngBlob, downloadBlob } from "../../components/svg-editor/svgUtils";
import GetIdCardTemplates from "../../actions/idcard/GetIdCardTemplates";
import SaveIdCardTemplate from "../../actions/idcard/SaveIdCardTemplate";

const IDCARD_HOST = "https://siliconmindstechnologies.com/";

// pull the student photo (data: URI / url) out of the base design's <pattern id="pattern">
const extractPhoto = (svg) => {
  const m = svg && svg.match(/<pattern id="pattern"[\s\S]*?<image[^>]*?(?:xlink:href|href)="([^"]+)"/i);
  return m ? m[1] : "";
};
// read the course title the backend rendered under the name
const extractCourse = (svg) => {
  try {
    const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
    const el = doc.getElementById("Full-Stack_Development");
    return el ? el.textContent.trim() : "";
  } catch (e) {
    return "";
  }
};

const StudentIDGenerator = ({ profile, Reload, saveidcard }) => {
  const device = DetectDevice();
  const { user } = useContext(AuthContext);

  const [selectedname, setselectedname] = useState(
    profile && profile.first_name ? (profile.first_name + " " + (profile.last_name || "")).trim() : ""
  );
  const [baseSvg, setBaseSvg] = useState("");
  const [svg, setSvg] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("default");

  const [toast, setToast] = useState(false);
  const [toastTitle, setToastTitle] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const notify = (t, m) => { setToastTitle(t); setToastMessage(m); setToast(true); };

  const joining = profile && profile.purchase_datetime ? dateFormat(profile.purchase_datetime, "d:mm:yyyy") : "";

  const tokenData = (photo, course) => ({
    name: selectedname || ((profile.first_name || "") + " " + (profile.last_name || "")).trim(),
    student_id: profile ? String(profile.purchase_id || "") : "",
    joining_date: joining,
    phone: profile ? String(profile.phone_number || "") : "",
    course: course || "",
  });

  // ---- base design (existing renderer, now with course title) -------
  const loadBase = async (nameOverride) => {
    if (!profile) return "";
    setLoading(true);
    const data = new FormData();
    data.append("username", profile.username || "");
    data.append("student_id", profile.purchase_id || "");
    data.append("image", profile.profile_pic || "");
    data.append("phone", profile.phone_number || "");
    data.append("joining_date", joining);
    data.append("course", profile.course_url || "");
    data.append("name", nameOverride != null ? nameOverride : selectedname);
    try {
      const res = await axios.post(IDCARD_HOST + "id-card-template.php", data, {
        headers: { "content-type": "multipart/form-data" },
      });
      setLoading(false);
      if (res && res.data && res.data.status === "success" && res.data.idcard) {
        setBaseSvg(res.data.idcard);
        return res.data.idcard;
      }
    } catch (e) {
      setLoading(false);
    }
    return "";
  };

  const loadTemplates = async () => {
    try {
      const res = await GetIdCardTemplates({
        payload: { session: user.SessionId, course_url: profile && profile.course_url },
      });
      if (res && res.status === "success") setTemplates(res.templates || []);
    } catch (e) {
      /* ignore */
    }
  };

  // initial + when preferred name changes: refresh the base design
  useEffect(() => {
    let active = true;
    (async () => {
      const base = await loadBase();
      if (!active) return;
      if (selectedTemplateId === "default") {
        setSvg(base);
      } else {
        applyTemplate(selectedTemplateId, base);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedname]);

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // parent can still trigger a save via the saveidcard prop
  useEffect(() => {
    if (saveidcard) saveIdCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveidcard]);

  const applyTemplate = (templateId, baseOverride) => {
    const base = baseOverride != null ? baseOverride : baseSvg;
    if (templateId === "default") { setSvg(base); return; }
    const t = templates.find((x) => String(x.id) === String(templateId));
    if (!t) { setSvg(base); return; }
    const photo = extractPhoto(base);
    const course = extractCourse(base);
    let out = substituteTokens(t.svg, tokenData(photo, course));
    out = out.split("{{photo}}").join(photo);
    setSvg(out);
  };

  const onSelectTemplate = (id) => {
    setSelectedTemplateId(id);
    applyTemplate(id);
  };

  // ---- actions ------------------------------------------------------
  const saveAsTemplate = async () => {
    if (!svg) return;
    const name = window.prompt("ID card template name (to reuse later):", "ID card design");
    if (!name) return;
    const photo = extractPhoto(svg);
    const course = extractCourse(svg) || extractCourse(baseSvg);
    let tpl = reverseTokens(svg, tokenData(photo, course));
    if (photo) tpl = tpl.split(photo).join("{{photo}}");
    const vb = getViewBox(svg);
    try {
      const res = await SaveIdCardTemplate({
        payload: {
          id: 0, name, course_url: profile && profile.course_url,
          svg: tpl, width: vb.width, height: vb.height, session: user.SessionId,
        },
      });
      if (res && res.status === "success") { notify("Saved", "ID card template saved for reuse."); loadTemplates(); }
      else notify("Error", (res && res.message) || "Could not save template.");
    } catch (e) { notify("Error", e.message); }
  };

  const saveIdCard = async () => {
    if (!svg || !profile) return;
    setSaving(true);
    const data = new FormData();
    data.append("username", profile.username || "");
    data.append("student_id", profile.purchase_id || "");
    data.append("image", profile.profile_pic || "");
    data.append("phone", profile.phone_number || "");
    data.append("joining_date", joining);
    data.append("course", profile.course_url || "");
    data.append("name", selectedname);
    data.append("save", true);
    data.append("custom_svg", svg);
    try {
      const res = await axios.post(IDCARD_HOST + "id-card-template.php", data, {
        headers: { "content-type": "multipart/form-data" },
      });
      setSaving(false);
      if (res && res.data && res.data.status === "success") {
        notify("ID Card Saved", "The ID card has been issued to the student.");
        if (Reload) Reload();
      } else {
        notify("Error", (res && res.data && res.data.msg) || "Could not save ID card.");
      }
    } catch (e) {
      setSaving(false);
      notify("Error", e.message);
    }
  };

  const downloadPng = async () => {
    if (!svg) return;
    try {
      const vb = getViewBox(svg);
      const blob = await svgToPngBlob(svg, vb.width, vb.height, 3);
      downloadBlob(blob, `idcard_${profile.purchase_id || "preview"}.png`);
    } catch (e) {
      // photo may be a cross-origin URL (tainted canvas) — fall back to SVG download
      downloadBlob(new Blob([svg], { type: "image/svg+xml" }), `idcard_${profile.purchase_id || "preview"}.svg`);
    }
  };

  const nameBtns = profile
    ? [
        { label: profile.first_name, value: profile.first_name },
        { label: profile.last_name, value: profile.last_name },
        { label: "Full name", value: (profile.first_name + " " + profile.last_name).trim() },
      ]
    : [];

  return (
    <div className="container-fluid">
      <DefaultAlertModal title={toastTitle} message={toastMessage} HandleClose={() => setToast(false)} show={toast} />

      <div className={device === "mobile" ? "" : "d-flex"}>
        {/* editor */}
        <div className={device === "mobile" ? "" : "col-lg-6 pe-lg-4"}>
          <h4 className="mb-3">ID Card</h4>
          {loading && !svg ? (
            <p className="text-muted">Loading ID card…</p>
          ) : svg ? (
            <SvgTemplateEditor value={svg} onChange={setSvg} previewMaxWidth={320} hint="Drag the name / course / details to reposition • double-click text to edit" />
          ) : (
            <p className="text-muted">Could not load the ID card design.</p>
          )}
        </div>

        {/* controls */}
        <div className={device === "mobile" ? "mt-3" : "col-lg-6 ps-lg-3"}>
          <h5>Preferred Name</h5>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {nameBtns.map((b, i) => (
              <button key={i} className="btn btn-outline-primary btn-sm" disabled={loading} onClick={() => setselectedname(b.value)}>
                {b.label}
              </button>
            ))}
          </div>

          <h6 className="mt-3 mb-1">Template</h6>
          <select className="form-control" value={selectedTemplateId} onChange={(e) => onSelectTemplate(e.target.value)}>
            <option value="default">Default design (current)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>

          <div className="d-flex flex-wrap mt-4" style={{ gap: 8 }}>
            <button className="btn btn-outline-secondary" onClick={saveAsTemplate} disabled={!svg}>💾 Save as Template</button>
            <button className="btn btn-outline-dark" onClick={downloadPng} disabled={!svg}>⬇ Download</button>
            <button className="btn btn-success" onClick={saveIdCard} disabled={!svg || saving}>
              {saving ? "Saving…" : "✔ Save ID Card (Issue to student)"}
            </button>
          </div>
          <p className="text-muted mt-2" style={{ fontSize: 13 }}>
            Saving issues the ID card to the student — it appears on their course dashboard for download.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentIDGenerator;
